/**
 * ReceptionPage - Pagină dedicată pentru personalul de recepție
 * Gestionează programările pacienților (înlocuiește worklist pentru recepție)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  CalendarMonth as CalendarIcon,
  TableChart as TableIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/ui';
import { getDevices } from '../services/devicesApi';
import { DeviceListItem } from '../types/devices';
import {
  getAppointments,
  checkinAppointment,
  startAppointment,
  completeAppointment,
  noshowAppointment,
  cancelAppointment,
} from '../services/appointmentsApi';
import { AppointmentListItem, APPOINTMENT_STATUS_CONFIG } from '../types/appointments';
import AppointmentFormDialog from '../components/appointments/AppointmentFormDialog';
import AppointmentsCalendarView from '../components/appointments/AppointmentsCalendarView';

const ReceptionPage: React.FC = () => {
  const { user } = useAuth();
  
  // View state
  const [activeView, setActiveView] = useState<'calendar' | 'table'>('calendar');
  
  // Data state
  const [appointments, setAppointments] = useState<AppointmentListItem[]>([]);
  const [appointmentsTotal, setAppointmentsTotal] = useState(0);
  const [devices, setDevices] = useState<DeviceListItem[]>([]);
  
  // Filters
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentListItem | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // Load initial data
  useEffect(() => {
    loadDevices();
  }, []);

  // Load appointments when filters change
  useEffect(() => {
    if (activeView === 'table') {
      loadAppointments();
    }
  }, [activeView, selectedDevice, selectedStatus, dateFrom, dateTo, page, rowsPerPage]);

  const loadDevices = async () => {
    try {
      const response = await getDevices({ limit: 100 });
      setDevices(response.items);
    } catch (err) {
      console.error('Error loading devices:', err);
    }
  };

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAppointments({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        device_id: selectedDevice || undefined,
        status: selectedStatus || undefined,
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      });
      setAppointments(response.items);
      setAppointmentsTotal(response.total);
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError('Eroare la încărcarea programărilor');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedDevice, selectedStatus, page, rowsPerPage]);

  // Action handlers
  const handleCheckin = async (apt: AppointmentListItem) => {
    try {
      await checkinAppointment(apt.id);
      loadAppointments();
    } catch (err) {
      setError('Eroare la check-in');
    }
  };

  const handleStart = async (apt: AppointmentListItem) => {
    try {
      await startAppointment(apt.id);
      loadAppointments();
    } catch (err) {
      setError('Eroare la începere');
    }
  };

  const handleComplete = async (apt: AppointmentListItem) => {
    try {
      await completeAppointment(apt.id);
      loadAppointments();
    } catch (err) {
      setError('Eroare la finalizare');
    }
  };

  const handleNoShow = async (apt: AppointmentListItem) => {
    try {
      await noshowAppointment(apt.id);
      loadAppointments();
    } catch (err) {
      setError('Eroare');
    }
  };

  const handleOpenCancelDialog = (apt: AppointmentListItem) => {
    setSelectedAppointment(apt);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedAppointment || !cancelReason.trim()) return;
    try {
      await cancelAppointment(selectedAppointment.id, cancelReason);
      setCancelDialogOpen(false);
      loadAppointments();
    } catch (err) {
      setError('Eroare la anulare');
    }
  };

  const handleAppointmentCreated = () => {
    setAppointmentDialogOpen(false);
    loadAppointments();
  };

  const canAddAppointment = user?.role && ['admin', 'operator', 'receptie'].includes(user.role);

  return (
    <Box>
      <PageHeader
        title="Recepție - Programări"
        actions={null}
      />

      <Box sx={{ px: 3, py: 0.6 }}>
        {/* Filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <TextField
            type="date"
            size="small"
            label="De la"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
          />
          <TextField
            type="date"
            size="small"
            label="Până la"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
          />

          <FormControl size="small" sx={{ minWidth: 150, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Aparat</InputLabel>
            <Select
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              label="Aparat"
            >
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
              {devices.map((d) => (
                <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.8rem' }}>
                  {d.code} - {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Toate</MenuItem>
              {Object.entries(APPOINTMENT_STATUS_CONFIG).map(([key, cfg]) => (
                <MenuItem key={key} value={key} sx={{ fontSize: '0.8rem' }}>
                  {cfg.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={activeView}
            exclusive
            onChange={(_, v) => v && setActiveView(v)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { py: 0.3, px: 1, fontSize: '0.75rem' } }}
          >
            <ToggleButton value="calendar">
              <CalendarIcon sx={{ fontSize: '1rem', mr: 0.5 }} />Calendar
            </ToggleButton>
            <ToggleButton value="table">
              <TableIcon sx={{ fontSize: '1rem', mr: 0.5 }} />Tabel
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flexGrow: 1 }} />

          {canAddAppointment && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAppointmentDialogOpen(true)}
              size="small"
              sx={{ fontSize: '0.78rem' }}
            >
              Programare Nouă
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Calendar View */}
        {activeView === 'calendar' && (
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent sx={{ p: 1.5 }}>
              <AppointmentsCalendarView
                deviceId={selectedDevice || undefined}
                onAppointmentClick={(apt) => console.log('Appointment clicked:', apt)}
                onCreateClick={() => setAppointmentDialogOpen(true)}
              />
            </CardContent>
          </Card>
        )}

        {/* Table View */}
        {activeView === 'table' && loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography color="text.secondary">Se încarcă programările...</Typography>
          </Box>
        )}

        {activeView === 'table' && !loading && appointments.length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" textAlign="center" color="text.secondary">
                Nu există programări pentru perioada selectată
              </Typography>
            </CardContent>
          </Card>
        )}

        {activeView === 'table' && !loading && appointments.length > 0 && (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.8rem' } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600 } }}>
                    <TableCell>Dată / Oră</TableCell>
                    <TableCell>Pacient</TableCell>
                    <TableCell>CNP</TableCell>
                    <TableCell>Telefon</TableCell>
                    <TableCell>Investigație</TableCell>
                    <TableCell>Aparat</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {appointments.map((apt) => (
                    <TableRow key={apt.id} hover>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {apt.scheduled_date}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          {apt.scheduled_start_time?.slice(0, 5)} - {apt.scheduled_end_time?.slice(0, 5)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                          {apt.patient_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {apt.patient_cnp || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {apt.patient_phone || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.8rem' }}>{apt.investigation_name}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                          ({apt.investigation_code})
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={apt.device_code} 
                          size="small" 
                          variant="outlined" 
                          sx={{ height: 18, fontSize: '0.68rem' }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={APPOINTMENT_STATUS_CONFIG[apt.status]?.label || apt.status}
                          color={APPOINTMENT_STATUS_CONFIG[apt.status]?.color || 'default'}
                          size="small"
                          sx={{ height: 18, fontSize: '0.68rem' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          {(apt.status === 'scheduled' || apt.status === 'confirmed') && (
                            <>
                              <Tooltip title="Check-in pacient">
                                <IconButton size="small" onClick={() => handleCheckin(apt)} color="primary">
                                  <ScheduleIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Pacient neprezent">
                                <IconButton size="small" onClick={() => handleNoShow(apt)} color="warning">
                                  <PersonIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Anulează programarea">
                                <IconButton size="small" onClick={() => handleOpenCancelDialog(apt)} color="error">
                                  <DeleteIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {apt.status === 'checked_in' && (
                            <Tooltip title="Începe examinarea">
                              <IconButton size="small" onClick={() => handleStart(apt)} color="primary">
                                <StartIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {apt.status === 'in_progress' && (
                            <Tooltip title="Finalizează examinarea">
                              <IconButton size="small" onClick={() => handleComplete(apt)} color="success">
                                <CompleteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={appointmentsTotal}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Pe pagină:"
              sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
            />
          </Paper>
        )}
      </Box>

      {/* Dialog pentru creare programare */}
      <AppointmentFormDialog
        open={appointmentDialogOpen}
        onClose={() => setAppointmentDialogOpen(false)}
        onSuccess={handleAppointmentCreated}
      />

      {/* Dialog anulare programare */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
          Anulare Programare
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem', mb: 2 }}>
            Anulați programarea pentru <strong>{selectedAppointment?.patient_name}</strong>?
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Motivul anulării *"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
            placeholder="Introduceți motivul anulării..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setCancelDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>
            Renunță
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            disabled={!cancelReason.trim()}
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            Anulează Programarea
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ReceptionPage;
